import React from "react"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { Chip } from "shared/ui/chip/Chip"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import type { useTabBarTabContainerDraggableViewModel } from "../model/useTabBarTabContainerDraggableViewModel"
import { TabBarMenuTab } from "./TabBarMenuTab"
import { TabBarSimpleTab } from "./TabBarSimpleTab"
import type { TabBarTabProps } from "./TabBarTab"

type DnDState = ReturnType<typeof useTabBarTabContainerDraggableViewModel>

export interface TabBarTabDivProps {
  tabId: string
  selected: boolean
  ariaControls: string | undefined
  ariaLabel: string | undefined
  disabled: boolean | undefined
  hasActions: boolean
  menuOpen: boolean
  className: string | undefined
  isSortable: boolean
  variant: TabBarTabProps["variant"]
  index: number
  isDragging: boolean
  insertBefore: boolean
  insertAfter: boolean
  icon: TabBarTabProps["icon"]
  count: TabBarTabProps["count"]
  displayLabel: string
  dndListeners: DnDState["dndListeners"] | undefined
  onClick: React.MouseEventHandler<HTMLDivElement>
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>
}

export const TabBarTabButton = React.forwardRef<
  HTMLDivElement,
  TabBarTabDivProps
>(
  (
    {
      tabId,
      selected,
      ariaControls,
      ariaLabel,
      disabled,
      hasActions,
      menuOpen,
      className,
      isSortable,
      variant,
      index,
      isDragging,
      insertBefore,
      insertAfter,
      icon,
      count,
      displayLabel,
      dndListeners,
      onClick,
      onKeyDown,
      ...rest
    },
    ref
  ) => (
    <div
      data-index={index}
      data-selected={selected ? "" : undefined}
      className={classNames(`tab-bar__tab nav-link ${className || ""}`, {
        "tab-bar__tab--sortable": isSortable,
        active: selected,
        "nav-link--warning": variant === "warning",
        "nav-link--error": variant === "error"
      })}
      {...dndListeners}
      {...rest}
      ref={ref}
      role="tab"
      id={tabId}
      aria-selected={selected}
      aria-controls={ariaControls}
      aria-label={ariaLabel}
      aria-disabled={disabled ? true : undefined}
      aria-haspopup={hasActions ? "menu" : undefined}
      aria-expanded={hasActions ? menuOpen : undefined}
      tabIndex={selected ? 0 : -1}
      onClick={onClick}
      onKeyDown={onKeyDown}
      data-dragging={isDragging ? "" : undefined}
      data-insert-before={insertBefore ? "" : undefined}
      data-insert-after={insertAfter ? "" : undefined}
    >
      {icon ? <Icon name={icon} size={IconSize.SMALL} /> : null}
      {count !== undefined ? (
        <Chip className="chip--count chip--subtle">{count}</Chip>
      ) : (
        displayLabel
      )}
      {hasActions ? (
        <span
          data-tab-kebab=""
          aria-hidden="true"
          className="tab-bar__tab-kebab"
        >
          <Icon name={IconName.KEBAB} size={IconSize.SMALL} />
        </span>
      ) : null}
    </div>
  )
)

TabBarTabButton.displayName = "TabBar.TabBarTabButton"

// Internal DnD override props — passed by TabBarSortableTab, not part of the public API
export interface TabBarBaseTabDnDOverrides {
  dndRef?: DnDState["dndRef"]
  dndListeners?: DnDState["dndListeners"]
  isDragging?: boolean
  insertBefore?: boolean
  insertAfter?: boolean
  isSortable?: boolean
}

export const TabBarBaseTab = React.memo(
  React.forwardRef<HTMLDivElement, TabBarTabProps & TabBarBaseTabDnDOverrides>(
    (props, ref) => {
      if (props.actions?.length) {
        return <TabBarMenuTab ref={ref} {...props} actions={props.actions} />
      }
      return <TabBarSimpleTab ref={ref} {...props} />
    }
  )
)

TabBarBaseTab.displayName = "TabBar.BaseTab"
